/**
 * FPS overlay — bottom-right pill with current frames-per-second and the
 * splat count being drawn. Shown via `ui: { fps: true }` on mount() or
 * `overlay.setVisible(true)`. No keyboard toggle: the old F key revealed it to
 * every visitor (removed 14/09/2026).
 *
 * Updates ~4Hz with a 30-frame rolling average so the digit doesn't jitter.
 */

import * as pc from 'playcanvas';

export class FpsOverlay {

    private root: HTMLDivElement;

    private fpsEl: HTMLSpanElement;

    private splatEl: HTMLSpanElement;

    private app: pc.AppBase;

    private frameTimes: number[] = [];

    private lastUpdate = 0;

    private maxSamples = 30;

    constructor(app: pc.AppBase, parent: HTMLElement) {
        this.app = app;

        this.root = document.createElement('div');
        this.root.className = 'fps-overlay';
        this.root.innerHTML = `
            <span class="fps"><span class="num">--</span><span class="unit">fps</span></span>
            <span class="sep">|</span>
            <span class="splats"><span class="num">0</span></span>
        `;
        this.fpsEl = this.root.querySelector('.fps .num') as HTMLSpanElement;
        this.splatEl = this.root.querySelector('.splats .num') as HTMLSpanElement;
        parent.appendChild(this.root);

        // Sample every frame; update DOM every ~250ms.
        app.on('framerender', () => this.onFrame());
    }

    private onFrame(): void {
        const now = performance.now();
        if (this.frameTimes.length > 0) {
            const last = this.frameTimes[this.frameTimes.length - 1];
            this.frameTimes.push(now);
            if (this.frameTimes.length > this.maxSamples) this.frameTimes.shift();
            // Throttle DOM writes to ~4Hz.
            if (now - this.lastUpdate > 250) {
                this.lastUpdate = now;
                const span = now - this.frameTimes[0];
                const frames = this.frameTimes.length - 1;
                const fps = (frames * 1000) / span;
                this.fpsEl.textContent = fps.toFixed(0);
                const splats = (this.app.stats?.frame as { gsplats?: number } | undefined)?.gsplats ?? 0;
                this.splatEl.textContent = splats >= 1_000_000
                    ? `${(splats / 1_000_000).toFixed(1)}M splats`
                    : splats >= 1_000
                        ? `${(splats / 1_000).toFixed(0)}K splats`
                        : `${splats} splats`;
                // Color-code FPS.
                this.root.classList.remove('good', 'ok', 'bad');
                if (fps >= 55) this.root.classList.add('good');
                else if (fps >= 30) this.root.classList.add('ok');
                else this.root.classList.add('bad');
                // discard last
                void last;
            }
        } else {
            this.frameTimes.push(now);
        }
    }

    setVisible(v: boolean): void {
        this.root.style.display = v ? '' : 'none';
    }

    isVisible(): boolean {
        return this.root.style.display !== 'none';
    }
}
